<template>
    <div
        class="app--field-document input-group"
        @dragover.prevent=""
        @dragenter="hover = true"
        @dragleave="hover = false"
        @drop="drop"
    >
        <div
            v-show="hover"
            ref="droppable"
            class="document-container droppable"
            :class="[classes, !multiple ? 'single' : '']"
        >
            <span class="icon"><AppIcon value="document"></AppIcon></span>
            <span class="label">
                <AppIcon value="upload"></AppIcon>
                {{ tDropToUpload }}
            </span>
        </div>
        <div
            v-if="empty && !hover"
            class="document-container empty"
            :class="[classes, !multiple ? 'single' : '']"
            @click.prevent="browse"
        >
            <span class="icon"><AppIcon value="document"></AppIcon></span>
            <span class="label">
                <AppIcon value="upload"></AppIcon>
                {{ tUpload }} {{ placeholder }}
            </span>
        </div>
        <div
            v-if="!empty && !hover"
            ref="list"
            class="document-container"
            :class="[classes, !multiple ? 'single' : '']"
        >
            <div class="list-group list-group-flush">
                <div
                    v-for="(item, i) in value" :key="i"
                    :class="{ downloadable: item.url }"
                    class="list-group-item d-flex justify-content-between align-items-center"
                >
                    <a v-if="item.url" :href="item.url" class="flex-grow-1">{{ item.filename }}</a>
                    <span v-if="!item.url" class="flex-grow-1">{{ item.filename }}</span>
                    <button
                        class="btn btn-sm btn-danger"
                        :class="{ 'disabled app--cursor-readonly': !enabled }"
                        :disabled="!enabled"
                        :title="tRemove"
                        @click.stop="remove(i)"
                    >
                        <AppIcon value="close"></AppIcon>
                    </button>
                </div>
            </div>

            <div v-if="multiple" class="app--with-tooltip">
                <button
                    class="btn more btn-block app--button primary w-100"
                    :class="{ 'disabled app--cursor-readonly': !enabled || value.length >= validateMaxFiles }"
                    :disabled="!enabled || value.length >= validateMaxFiles"
                    @click.prevent="browse"
                >
                    <span class="label">
                        <AppIcon value="upload"></AppIcon>
                        {{ tAddMore }}
                    </span>
                </button>
                <span v-if="value.length >= validateMaxFiles" class="app--tooltip tooltip-xsmall shadow rounded top">
                    Only {{ validateMaxFiles }} {{ validateMaxFiles === 1 ? 'file is' : 'files are' }} allowed in this
                    field.
                </span>
            </div>
        </div>
        <input
            ref="input"
            v-validate="validate"
            type="file"
            class="form-control no-validate"
            :required="validateRequired && empty"
            :accept="validateType"
            multiple
            :placeholder="placeholder"
            @change="load"
        />
        <div ref="feedback" class="invalid-feedback"></div>
    </div>
</template>

<script>
    export default {
        props: {
            value: {
                type: Array,
                default: () => []
            },
            placeholder: {
                type: String,
                default: ''
            },
            enabled: {
                type: Boolean,
                default: true
            },
            classes: {
                type: String,
                default: ''
            },
            validateRequired: {
                type: Boolean,
                default: false
            },
            validateType: {
                type: String,
                default: '.jpg, .jpeg, .png, .gif, .bmp, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .zip, .pdf, .csv'
            },
            validateMaxFiles: {
                type: Number,
                default: 10
            },
            validateSize: {
                type: Number,
                default: 1024 * 32
            },
            multiple: {
                type: Boolean,
                default: true
            },
            tDropToUpload: {
                type: String,
                default: 'Drop to upload'
            },
            tAddMore: {
                type: String,
                default: 'Add more'
            },
            tUpload: {
                type: String,
                default: 'Upload'
            },
            tRemove: {
                type: String,
                default: 'Remove'
            }
        },
        emits: ['update:value'],
        data() {
            return { hover: false, totalSize: 100 * 1024 * 1024 }
        },
        computed: {
            empty() {
                return this.value.length == 0
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(val) {
                    this.$nextTick(() => {
                        this.$refs.droppable.style.height = this.$refs.list
                            ? this.$refs.list.clientHeight + 'px'
                            : 'auto'
                        if (this.$refs.input && this.$refs.input.validity.valid == true) {
                            this.$refs.feedback.classList.remove('d-inline-block')
                        }
                    })
                }
            }
        },
        methods: {
            browse: function (event) {
                if (!this.enabled) return
                this.$refs.input.click()
            },
            drop: function (event) {
                event.preventDefault()
                this.hover = false

                if (!this.enabled || !event.dataTransfer) return
                let result = [...this.value]
                let currentSize = 0
                let files = event.dataTransfer.items ? [...event.dataTransfer.items] : [...event.dataTransfer.files]
                for (let i = 0; i < files.length; i++) {
                    if (files[i].kind === 'file') {
                        files[i] = files[i].getAsFile()
                    }
                    if (result.length >= this.validateMaxFiles) {
                        console.error('Maximum number of files exceeded.')
                        break
                    }
                    if (this.validateSize > 0 && files[i].size / 1024 > this.validateSize) {
                        console.log('File size not allowed: ' + files[i].name)
                        continue
                    }
                    if (this.validateType) {
                        let types = this.validateType.split(',').map(el => el.trim())
                        let ext = files[i].name.substring(files[i].name.lastIndexOf('.'))
                        if (types.indexOf(ext) === -1) {
                            console.log('File type not allowed: ' + files[i].name)
                            continue
                        }
                    }
                    if (currentSize + files[i].size > this.totalSize) {
                        console.log('Total size cannot exceed 100MB. Skipping: ' + files[i].name)
                        continue
                    }
                    currentSize += files[i].size
                    result.push({ filename: files[i].name, url: null, file: files[i] })
                }
                if (result.length !== this.value.length) {
                    this.$emit('update:value', result)
                }
            },
            load(event) {
                if (!this.$validate(event)) {
                    this.file = ''
                    event.target.value = ''
                    return
                }
                let files = event.target.files

                if (this.value.length + files.length > this.validateMaxFiles) {
                    console.error('Cannot exceed the maximum number of files.')
                    return
                }

                let result = [...this.value]
                let currentSize = 0
                for (let i = 0; i < files.length; i++) {
                    if (currentSize + files[i].size > this.totalSize) {
                        console.log('Total size cannot exceed 100MB. Skipping: ' + files[i].name)
                        continue
                    }
                    currentSize += files[i].size
                    result.push({ filename: files[i].name, url: null, file: files[i] })
                }
                this.$emit('update:value', result)
            },
            remove(index) {
                if (!this.enabled) return
                this.$emit('update:value', this.value.filter((_, i) => i !== index))
            },
            validate(event) {
                let message = ''
                if (event.target.files.length > 0) {
                    let files = event.target.files
                    if (this.value.length + files.length > this.validateMaxFiles) {
                        message = `Maximum of ${this.validateMaxFiles} files exceeded.`
                    } else {
                        for (let i = 0; i < files.length; i++) {
                            if (this.validateSize > 0 && files[i].size / 1024 > this.validateSize) {
                                message = this.$t('validation.max.file', {
                                    attribute: this.placeholder,
                                    max: this.validateSize
                                })
                            }
                        }
                    }
                }
                event.target.setCustomValidity(message)
            }
        }
    }
</script>
